/**
 * useStableSearchParams — Drop-in replacement for React Router's useSearchParams
 * that returns a referentially stable setter function.
 *
 * Tier 3 — Platform Packages (@plerity/react-shared)
 *
 * ## Why This Exists
 *
 * React Router's `useSearchParams()` may return a new `setSearchParams` function
 * reference on every render. If that setter (or a function derived from it) is
 * included in a `useEffect` dependency array, it causes an infinite loop:
 *
 *   render → new setSearchParams → effect fires → navigation → re-render → ...
 *
 * This hook fixes both problems:
 * 1. **Stable setter** — uses a ref so the returned function never changes identity
 * 2. **No-op guard** — compares serialized params before/after and skips the
 *    navigation if nothing changed (prevents the initial-render loop where
 *    setSearch("") deletes a param that doesn't exist)
 *
 * ## Incident Reference
 *
 * 2026-04-08: CRM app had all 5 list pages frozen for ~15s because
 * `useListPageState()` returned unstable setters used in `useEffect` deps.
 * Chrome throttled after ~50 rapid `history.replaceState` calls.
 *
 * @example
 * ```tsx
 * const [searchParams, setSearchParams] = useStableSearchParams();
 *
 * // Safe in useEffect deps — setSearchParams is permanently stable
 * const setPage = useCallback((p: number) => {
 *   setSearchParams((prev) => {
 *     const next = new URLSearchParams(prev);
 *     next.set("page", String(p));
 *     return next;
 *   }, { replace: true });
 * }, [setSearchParams]); // stable — never triggers re-run
 * ```
 */

import { useCallback, useContext, useEffect, useRef } from "react";
import {
  type NavigateOptions,
  UNSAFE_NavigationContext,
  type URLSearchParamsInit,
  useSearchParams,
} from "react-router-dom";

type SetSearchParamsUpdater = URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit);

type StableSetSearchParams = (updater: SetSearchParamsUpdater, opts?: NavigateOptions) => void;

/**
 * A write that has been handed to the router but not yet rendered. Module-level on purpose: the
 * sort hook, the pagination hook and a typed-params hook each own a separate `useStableSearchParams`
 * instance, and a sort change immediately followed by its page reset (one event handler, one tick)
 * must compose across those instances instead of the second overwriting the first from the same
 * stale render snapshot. Cleared on the next committed render, and ignored after a short TTL in case
 * the navigation was blocked and never rendered.
 *
 * Scoped to the router that issued it (`owner` = that router's navigator, shared by every hook
 * instance inside one router): a different router — a remount, a second `<MemoryRouter>`, the next
 * test — must never treat another router's unrendered write as already applied, or its own first
 * write (e.g. an auto-select effect) is skipped as a no-op.
 */
interface PendingWrite {
  owner: object;
  from: string;
  to: URLSearchParams;
  at: number;
}
let pendingWrite: PendingWrite | null = null;
const PENDING_WRITE_TTL_MS = 100;

export function useStableSearchParams(): [URLSearchParams, StableSetSearchParams] {
  const [searchParams, setSearchParams] = useSearchParams();
  const owner: object = useContext(UNSAFE_NavigationContext).navigator;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;

  // Ref to the latest setter — avoids stale closures if React Router
  // returns a new function on re-render.
  const setRef = useRef(setSearchParams);
  setRef.current = setSearchParams;

  // Ref to the latest params — used for no-op comparison.
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  const renderedRaw = searchParams.toString();
  useEffect(() => {
    // The router has rendered the pending write (or moved elsewhere): the snapshot is current again.
    if (
      pendingWrite &&
      pendingWrite.owner === owner &&
      (renderedRaw === pendingWrite.to.toString() || renderedRaw !== pendingWrite.from)
    ) {
      pendingWrite = null;
    }
  }, [renderedRaw, owner]);

  const stableSet: StableSetSearchParams = useCallback(
    (updater, opts) => {
      const rendered = paramsRef.current;
      const renderedKey = rendered.toString();
      const currentOwner = ownerRef.current;
      // Base the update on a write issued earlier in this tick by THIS router, if not yet rendered.
      const base =
        pendingWrite &&
        pendingWrite.owner === currentOwner &&
        pendingWrite.from === renderedKey &&
        Date.now() - pendingWrite.at < PENDING_WRITE_TTL_MS
          ? pendingWrite.to
          : rendered;

      // Resolve the next params value
      const resolved = typeof updater === "function" ? updater(new URLSearchParams(base)) : updater;

      // Normalize to URLSearchParams for comparison
      const next =
        resolved instanceof URLSearchParams
          ? resolved
          : new URLSearchParams(resolved as Record<string, string> | string[][] | string);

      // Skip no-op navigations — prevents infinite loops from effects that
      // call setSearchParams with the same values that are already in the URL.
      if (next.toString() === base.toString()) {
        return;
      }

      pendingWrite = { owner: currentOwner, from: renderedKey, to: next, at: Date.now() };
      setRef.current(next, opts);
    },
    [] // Truly stable — uses refs for all external values
  );

  return [searchParams, stableSet];
}
